import { useMemo, useState } from 'react';

import { ApiClient, formatDate, formatMoney, useAsync } from '@clientma/core';
import { Alert, Badge, Card, Input, Select, Skeleton } from '@clientma/ui';

import { providerApi, type ProviderRenewalRow } from '../api';

/**
 * What the subscription clock is about to do, and what it already did that nobody paid for.
 *
 * The renewals themselves are automatic — a term reaching its end raises the next one and
 * invoices it without anybody here pressing anything. This screen exists because "automatic"
 * is only safe when somebody can see it: a customer about to be billed for eighty seats they
 * stopped using, or one whose grace period runs out on Friday, is a conversation, not a job.
 *
 * Ordered by date with anything already past it first, because the useful question is never
 * "what renews this month" — it is "who do I need to speak to before something happens".
 */
export default function Renewals({ api }: { api: ApiClient }) {
  const provider = useMemo(() => providerApi(api), [api]);

  const [withinDays, setWithinDays] = useState(30);
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useAsync(
    (signal) => provider.renewals(withinDays, signal),
    [provider, withinDays],
  );

  const rows = useMemo(() => {
    const all = data ?? [];
    const term = search.trim().toLowerCase();

    const matched = term
      ? all.filter((row) =>
          [row.tenantName, row.planName, row.billingEmail, row.unpaidInvoiceNumber].some(
            (field) => field?.toLowerCase().includes(term),
          ),
        )
      : all;

    return [...matched].sort((a, b) => a.daysUntilRenewal - b.daysUntilRenewal);
  }, [data, search]);

  const owing = rows.filter((row) => row.unpaidIsOverdue);
  const stopping = rows.filter((row) => row.cancelAtPeriodEnd);
  const soon = rows.filter((row) => row.daysUntilRenewal >= 0 && row.daysUntilRenewal <= 7);

  if (isLoading) return <Skeleton height={280} />;

  if (error) {
    return (
      <Alert tone="danger" title="The renewal list could not be loaded">
        {error.message}
      </Alert>
    );
  }

  return (
    <div className="cma-stack">
      <div className="provider-grid">
        <Tile label="In the window" value={rows.length} />
        <Tile label="Renewing within 7 days" value={soon.length} tone={soon.length ? 'warning' : undefined} />
        <Tile label="Unpaid and overdue" value={owing.length} tone={owing.length ? 'danger' : undefined} />
        <Tile label="Set to stop" value={stopping.length} />
      </div>

      <div className="provider-toolbar">
        <Input
          type="search"
          value={search}
          placeholder="Search by customer, plan, email or invoice…"
          aria-label="Search renewals"
          onChange={(event) => setSearch(event.target.value)}
        />

        <Select
          value={String(withinDays)}
          aria-label="How far ahead to look"
          onChange={(event) => setWithinDays(Number(event.target.value))}
        >
          <option value="7">Next 7 days</option>
          <option value="30">Next 30 days</option>
          <option value="90">Next 90 days</option>
          <option value="365">Next year</option>
        </Select>
      </div>

      {rows.length === 0 ? (
        <Alert tone="success" title="Nothing in that window">
          No subscription renews, lapses or stops in the next {withinDays} days.
        </Alert>
      ) : (
        rows.map((row) => <RenewalCard key={row.subscriptionId} row={row} />)
      )}
    </div>
  );
}

function RenewalCard({ row }: { row: ProviderRenewalRow }) {
  const overdue = row.daysUntilRenewal < 0;

  return (
    <Card>
      <div className="cma-stack cma-stack--tight">
        <div className="provider-actionrow">
          <div>
            <div className="provider-strong">{row.tenantName}</div>
            <div className="provider-muted">
              {row.planName ?? 'No plan'} · {row.interval} · {row.seats} seat
              {row.seats === 1 ? '' : 's'}
            </div>
          </div>

          <div className="provider-amount">{formatMoney(row.termAmount, row.currency)}</div>
        </div>

        <div className="provider-chips">
          <Badge tone={statusTone(row.status)}>{row.status}</Badge>
          {row.cancelAtPeriodEnd && <Badge tone="warning">Stopping</Badge>}
          {row.unpaidIsOverdue && <Badge tone="danger">Payment overdue</Badge>}
        </div>

        <div>
          <div className="provider-linerow">
            <span className="provider-muted">
              {row.cancelAtPeriodEnd ? 'Stops on' : 'Renews on'}
            </span>
            <span>
              {formatDate(row.renewsAtUtc)}
              {overdue
                ? ` — ${Math.abs(row.daysUntilRenewal)} day${
                    Math.abs(row.daysUntilRenewal) === 1 ? '' : 's'
                  } ago`
                : ` — in ${row.daysUntilRenewal} day${row.daysUntilRenewal === 1 ? '' : 's'}`}
            </span>
          </div>

          {row.gracePeriodEndsAtUtc && (
            <div className="provider-linerow">
              <span className="provider-muted">Access closes</span>
              <span>{formatDate(row.gracePeriodEndsAtUtc)}</span>
            </div>
          )}

          {row.unpaidInvoiceNumber && (
            <div className="provider-linerow">
              <span className="provider-muted">Outstanding</span>
              <span className={row.unpaidIsOverdue ? 'provider-owing' : undefined}>
                {row.unpaidInvoiceNumber} — {formatMoney(row.unpaidAmount ?? 0, row.currency)}
                {row.unpaidIsOverdue ? ' (overdue)' : ''}
              </span>
            </div>
          )}

          <div className="provider-linerow">
            <span className="provider-muted">Billing contact</span>
            <span>{row.billingEmail ?? 'Not set'}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'warning' | 'danger';
}) {
  return (
    <Card>
      <div className="provider-tile__label">{label}</div>
      <div className={`provider-tile__value${tone ? ` provider-tile__value--${tone}` : ''}`}>
        {value.toLocaleString()}
      </div>
    </Card>
  );
}

function statusTone(status: string) {
  if (status === 'Active') return 'success' as const;
  if (status === 'PastDue') return 'danger' as const;
  if (status === 'Trialing') return 'info' as const;

  return 'neutral' as const;
}
